import type { PossibleFilterOperationsType } from 'src/modals/storageFilters/types'
import type { ValueTypes } from '../../types'

/**
 * Filter DSL of the storage service (ObjectsFiltersService).
 *
 * The root is a group; every node recurses. The dashboard additionally stores
 * UI metadata in `additionalParams`, which is persisted with smart lists and
 * exports but stripped before list requests (see `stripFilterMetadata`).
 */

/** Logic of a group, `and` is the backend default. */
export type TFilterLogic = 'and' | 'or'

export type TFilterOperation = `${PossibleFilterOperationsType}`

export type TFilterValue =
  | string
  | number
  | boolean
  | null
  | Array<string | number>

export interface IFilterPartyData {
  avatarUrl?: string
  name?: string
}

export type TFilterRelationKind = 'parent' | 'children' | 'field' | 'reverse'

/** How a relation node was built in the drawer, used by the chips bar and `setRootLogic`. */
export interface IFilterRelationMeta {
  kind: TFilterRelationKind
  schemeTitle?: string
  fieldTitle?: string
  /** The nested group of the node mirrors the root logic and follows it when the root changes. */
  mirrorRoot?: boolean
}

/** UI-only metadata of a node; never interpreted by the backend. */
export interface IFilterMeta {
  operationInfo?: { id?: number; text?: string }
  schemeInfo?: { schemeTitle?: string; valueType?: `${ValueTypes}` | null }
  userData?: IFilterPartyData
  relationData?: IFilterPartyData
  relation?: IFilterRelationMeta
}

/** Condition on a field of the filtered object. No operation and empty value means "exists". */
export interface IFilterLeaf {
  field: string
  operation?: TFilterOperation
  value?: TFilterValue
  insensitive?: boolean
  negation?: boolean
  additionalParams?: IFilterMeta
}

export interface IFilterGroup {
  logic?: TFilterLogic
  negation?: boolean
  conditions: IFilterNode[]
  additionalParams?: IFilterMeta
}

/**
 * "At least one object referenced through <field> (a relationship field of the
 * current scheme) matches <conditions>". The node itself carries no operation.
 */
export interface IFilterRelationshipFieldNode {
  field: string
  conditions: IFilterNode
  negation?: boolean
  additionalParams?: IFilterMeta
}

/**
 * Parent / children relation (`relationship` only, the backend resolves the
 * direction) or a reverse reference (`relationship` + `viaField`: objects of
 * that scheme pointing at the current one through their relationship field).
 * Without `conditions` the node means "the relation exists".
 */
export interface IFilterRelationNode {
  relationship: string
  viaField?: string
  conditions?: IFilterNode
  negation?: boolean
  additionalParams?: IFilterMeta
}

export type IFilterNode =
  | IFilterLeaf
  | IFilterGroup
  | IFilterRelationshipFieldNode
  | IFilterRelationNode

export interface IStorageFilter {
  logic?: TFilterLogic
  negation?: boolean
  conditions: IFilterNode[]
}

/**
 * @deprecated Legacy name of the filter root; use `IStorageFilter` (root) and
 * `IFilterNode` (nodes). Kept as an alias for one release.
 */
export type IStorageFilterRule = IStorageFilter

export const isFilterGroup = (node: IFilterNode): node is IFilterGroup =>
  Array.isArray((node as IFilterGroup).conditions)

export const isFilterRelationNode = (
  node: IFilterNode
): node is IFilterRelationNode =>
  typeof (node as IFilterRelationNode).relationship === 'string'

export const isFilterRelationshipFieldNode = (
  node: IFilterNode
): node is IFilterRelationshipFieldNode => {
  const { field, conditions } = node as IFilterRelationshipFieldNode

  return (
    typeof field === 'string' &&
    !isFilterRelationNode(node) &&
    Boolean(conditions) &&
    typeof conditions === 'object' &&
    !Array.isArray(conditions)
  )
}

export const isFilterLeaf = (node: IFilterNode): node is IFilterLeaf =>
  !isFilterGroup(node) &&
  !isFilterRelationNode(node) &&
  !isFilterRelationshipFieldNode(node)

/**
 * Removes `additionalParams` from the whole tree, recursing into both group
 * (array) and relation (single object) conditions. Everything else is kept as is.
 */
export function stripFilterMetadata<T extends IStorageFilter | IFilterNode>(
  node: T
): T {
  const copy = { ...node } as T & {
    additionalParams?: IFilterMeta
    conditions?: IFilterNode[] | IFilterNode
  }

  delete copy.additionalParams

  if (Array.isArray(copy.conditions)) {
    copy.conditions = copy.conditions.map((child) => stripFilterMetadata(child))
  } else if (copy.conditions && typeof copy.conditions === 'object') {
    copy.conditions = stripFilterMetadata(copy.conditions)
  }

  return copy
}
